import { Moment } from "moment"
import { Uuid } from "helpers/types/uuid"
import { IsoDate } from "helpers/types/dates"
import useResource from "hooks/resources"
import { useAccountAlias } from "resources/account"
import { ResourceResult } from "helpers/data-statuses"

export interface Portfolio {
  alias: string
  description: string
  fxHedgeEnabled: boolean
  id: Uuid
  intradayEnabled: boolean
  lastUpdated: IsoDate | null
  live: boolean
  modelId: string
  name: string
  shortId: string

  // These types not validated
  betaBenchmarkId: string | null
  betaIndexId: string | null
  betaResourceId: string | null
  betaResourceType: string | null
  betaSecurityId: string | null
  currencyOverride: string | null
}

export function usePortfolios(): ResourceResult<Portfolio[]> {
  const account = useAccountAlias()

  return useResource<Portfolio[]>({
    resource: "portfolios",
    url: () => account ? `/api/${account}/portfolios` : null,
  }, "portfolios")
}

export interface PortfolioUploadDates {
  availableFrom: IsoDate
  latestUploadedDate: IsoDate
}

// Portfolio's own upload-date range, independent of any selected model — the REST detail
// endpoint underneath this (unlike position-set metadata) needs no modelId to resolve, so the
// coverage gate can compute windowed dates before a model has ever been picked.
export function usePortfolioUploadDates(portfolioId: string | null): ResourceResult<PortfolioUploadDates> {
  const account = useAccountAlias()

  return useResource<PortfolioUploadDates>({
    resource: "portfolio",
    url: () => (account && portfolioId ? `/api/${account}/p/${portfolioId}` : null),
  }, "portfolio")
}

export function useHasUnexpandedComposites(modelId: string, portfolioId: Uuid, currentDate: Moment, performanceStartDate?: Moment) {
  const startDate = performanceStartDate || currentDate
  const account = useAccountAlias()

  return useResource<boolean>({
    resource: "hasUnexpandedComposites",
    url: () => account && modelId && portfolioId && currentDate ? `/api/${account}/p/${portfolioId}/${modelId}/unexpanded-composites?from=${startDate.format("YYYY-MM-DD")}&to=${currentDate.format("YYYY-MM-DD")}` : null,
  }, "hasUnexpandedComposites")
}
